import { useQuery } from "@apollo/client";
import GET_THIRD_PARTY_ORDERS from "./getThirdPartyOrder.gql";
import { LandTechEndpoints, routedClientWithoutTypenames } from "react-migration/lib/persistence/apollo";

// Provider type
export interface ThirdPartyProvider {
  id: string;
  name: string;
  displayName: string;
}

// Deliverable type
export interface ThirdPartyOrderDeliverable {
  id: string;
  signedUrl: {
    url: string;
    expiresAt: string;
  };
  filename: string;
  type: "PDF" | "JSON" | "GEOJSON" | "PERMALINK";
}

// Order status enum
export enum ThirdPartyOrderStatus {
  PLACED = "PLACED",
  PROCESSING = "PROCESSING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  CANCELED = "CANCELED",
}

// Third party order type
export interface ThirdPartyOrder {
  id: string;
  thirdPartyOrderId: string;
  siteId: string;
  quote: {
    id: string;
    discountPercentage?: number;
  };
  status: ThirdPartyOrderStatus;
  estimatedCompletedAt: string; // ISO date string
  completedAt: string; // ISO date string
  totalAmount: number;
  currency: string;
  teamId: string;
  userId: string;
  createdAt: string; // ISO date string
  deliverables: ThirdPartyOrderDeliverable[];
  provider: ThirdPartyProvider;
}

// Query input type
export interface GetThirdPartyOrderInput {
  orderId?: string;
  siteId?: string;
}

// Query response type
export interface GetThirdPartyOrdersData {
  getThirdPartyOrders: ThirdPartyOrder[];
}

// Query variables type
export interface GetThirdPartyOrdersVariables {
  input: GetThirdPartyOrderInput;
}

/**
 * Hook for fetching third-party orders by ID and/or siteId
 * @param options - Query options including orderId and/or siteId
 * @param skip - Whether to skip the query (optional, defaults to false)
 * @returns Query result with the orders data, loading state, and error
 */
export const useThirdPartyOrders = (options: { orderId?: string; siteId?: string } = {}, skip = false) => {
  return useQuery<GetThirdPartyOrdersData, GetThirdPartyOrdersVariables>(GET_THIRD_PARTY_ORDERS, {
    variables: {
      input: {
        orderId: options.orderId,
        siteId: options.siteId,
      },
    },
    client: routedClientWithoutTypenames,
    context: {
      endpoint: LandTechEndpoints.Sites,
    },
    skip,
    fetchPolicy: "network-only",
  });
};
